/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument, TieredPackages } from 'paintscout'
import type { QuoteTemplateOption, TieredPackagePresetOption } from '../index'
import { tieredPackagesFromPreset } from './to-tiered-packages'

/**
 * One saved package template by key, or undefined when it no longer exists.
 *
 * Unfiltered on purpose — this resolves a link that was already made, so it must not apply the
 * quote-type and active filtering `getTieredPackagePresets` uses to build a picker. A template
 * linked to a package template keeps working even if that package template later stops matching
 * the estimate's type.
 */
export function getTieredPackagePreset(args: {
  options: OptionsDocument
  key?: string
}): TieredPackagePresetOption | undefined {
  const { options, key } = args

  if (!key) {
    return undefined
  }

  const preset = options?.options?.tieredPackagePresets?.values?.[key]

  return preset && !preset._deleted ? preset : undefined
}

/**
 * Which package template a quote template links, if any.
 *
 * The link lives on the quote template's options entry (`tieredPackagePresetKey`), because a key is
 * all it is: the package template itself stays in Settings and is read live. Estimate templates
 * built before that fell back to a whole copy of the set on the template document, so the key inside
 * that copy is the second place to look — read-only back-compat, never written any more (ENG-1629).
 */
export function getTemplateTieredPackagePresetKey(args: {
  template?: Pick<QuoteTemplateOption, 'tieredPackagePresetKey'> | null
  /** The quote-template document, for the legacy copy. */
  quote?: Pick<QuoteDocument, 'tieredPackages'> | null
}): string | undefined {
  const { template, quote } = args

  return template?.tieredPackagePresetKey ?? quote?.tieredPackages?.presetKey
}

/**
 * The package set a quote template hands down, resolved live from Settings.
 *
 * The point of resolving rather than reading a stored set: editing a package template in Settings
 * has to show up on every quote template that links it, and on every estimate made from one
 * afterwards (ENG-1629, ENG-1630). A key that no longer resolves — the package template was
 * deleted — is treated as no link at all, so deleting one never breaks a quote template.
 *
 * Comes back `enabled: true` (from `tieredPackagesFromPreset`) and selected on the base tier, which
 * is what an estimate wants. The quote template's own View ignores `enabled` — a template shows the
 * set, it does not price one.
 */
export function getTemplateTieredPackages(args: {
  template?: QuoteTemplateOption | null
  quote?: Pick<QuoteDocument, 'tieredPackages'> | null
  options: OptionsDocument
}): TieredPackages | undefined {
  const { template, quote, options } = args

  const preset = getTieredPackagePreset({
    options,
    key: getTemplateTieredPackagePresetKey({ template, quote })
  })

  return preset ? tieredPackagesFromPreset({ preset }) : undefined
}
